/* @jsx h */
import { defineComponent, h, computed } from "vue";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import type { DateRangeType } from "@lemmonhq/types";
import { useLocalization } from "../Localization";
import { Calendar } from "./Calendar";
import { RangeFields, type RangeEnd } from "./RangeFields";
import type { DateRangeOption, TimeOfDay } from "./types";

interface RangeFooterProps {
  startDate: string;
  endDate: string;
  onCancel: () => void;
  onApply: () => void;
  canApply: boolean;
}

/**
 * Shared between the desktop dropdown (rendered inline at the bottom of the
 * panel) and the mobile sheet (rendered into `LemmonModal`'s sticky
 * `renderButtons` slot so Apply is never scrolled off screen).
 */
export const RangeFooter = defineComponent<RangeFooterProps>({
  name: "RangeFooter",
  props: ["startDate", "endDate", "onCancel", "onApply", "canApply"],
  setup(props) {
    const { t } = useLocalization();

    const dayCount = computed(() =>
      Math.max(1, dayjs(props.endDate).diff(dayjs(props.startDate), "day") + 1),
    );

    return () => (
      <div class="flex items-center justify-between gap-3 border-t  bg-base-300 px-3 py-2.5 sm:px-4">
        <span class="truncate text-xs font-medium text-base-content/50">
          {dayCount.value === 1
            ? t("date_range_picker.modal.one_day")
            : t("date_range_picker.modal.day_count", [dayCount.value])}
        </span>
        <div class="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={props.onCancel}
            class="rounded-lg px-3 py-1.5 text-[13px] font-medium text-base-content/60 transition-colors hover:bg-base-200 hover:text-base-content"
          >
            {t("date_range_picker.modal.cancel")}
          </button>
          <button
            type="button"
            onClick={props.onApply}
            disabled={!props.canApply}
            class="rounded-lg bg-primary px-4 py-1.5 text-[13px] font-semibold text-primary-content transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {t("date_range_picker.modal.apply")}
          </button>
        </div>
      </div>
    );
  },
});

interface DateRangePanelProps {
  options: DateRangeOption[];
  selectedType: DateRangeType;
  startDate: string;
  endDate: string;
  startTime: TimeOfDay;
  endTime: TimeOfDay;
  activeEnd: RangeEnd;
  showTime: boolean;
  leftMonth: Dayjs;
  rightMonth: Dayjs;
  isMobile: boolean;
  presetsOnly: boolean;
  onActivate: (end: RangeEnd) => void;
  onPresetClick: (type: DateRangeType) => void;
  onDateClick: (date: string) => void;
  onStartTimeChange: (value: TimeOfDay) => void;
  onEndTimeChange: (value: TimeOfDay) => void;
  onLeftMonthChange: (month: Dayjs) => void;
  onRightMonthChange: (month: Dayjs) => void;
}

export const DateRangePanel = defineComponent<DateRangePanelProps>({
  name: "DateRangePanel",
  props: [
    "options",
    "selectedType",
    "startDate",
    "endDate",
    "startTime",
    "endTime",
    "activeEnd",
    "showTime",
    "leftMonth",
    "rightMonth",
    "isMobile",
    "presetsOnly",
    "onActivate",
    "onPresetClick",
    "onDateClick",
    "onStartTimeChange",
    "onEndTimeChange",
    "onLeftMonthChange",
    "onRightMonthChange",
  ],
  setup(props) {
    const { t } = useLocalization();

    const header = () => (
      <div class="border-b  px-3 py-3 bg-base-300">
        <RangeFields
          startDate={props.startDate}
          endDate={props.endDate}
          startTime={props.startTime}
          endTime={props.endTime}
          active={props.activeEnd}
          showTime={props.showTime}
          onActivate={props.onActivate}
          onStartTimeChange={props.onStartTimeChange}
          onEndTimeChange={props.onEndTimeChange}
        />
      </div>
    );

    /* ── Mobile: horizontally scrolling chip row, never wraps ─────────── */
    const presetChips = () => (
      <div class="border-b  px-3 py-2.5 bg-base-300">
        <div class="mb-2 text-[10px] font-semibold uppercase tracking-wider text-base-content/35">
          {t("date_range_picker.modal.quick_select")}
        </div>
        <div
          class="-mx-3 flex gap-2 overflow-x-auto px-3 pb-1"
          style="scrollbar-width: none; -ms-overflow-style: none;"
        >
          {props.options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => props.onPresetClick(option.value)}
              class={[
                "shrink-0 whitespace-nowrap rounded-full px-3.5 py-1.5",
                "text-[13px] font-medium transition-colors duration-150",
                props.selectedType === option.value
                  ? "bg-primary text-primary-content"
                  : "bg-base-200/70 text-base-content/70 hover:bg-base-200 hover:text-base-content",
              ].join(" ")}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    );

    /* ── Desktop: vertical rail, scannable, never wraps ───────────────── */
    const presetRail = () => (
      <div class="flex w-[148px] shrink-0 flex-col gap-px overflow-y-auto border-r bg-base-300 p-2">
        {props.options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => props.onPresetClick(option.value)}
            class={[
              "flex items-center justify-between gap-2 rounded-lg px-2.5 py-[7px]",
              "text-left text-[13px] transition-colors duration-150",
              props.selectedType === option.value
                ? "bg-primary/10 font-semibold text-primary"
                : "font-medium text-base-content/65 hover:bg-base-200 hover:text-base-content",
            ].join(" ")}
          >
            <span class="truncate">{option.label}</span>
            {props.selectedType === option.value && (
              <span class="material-symbols-outlined shrink-0 text-[15px]">
                check
              </span>
            )}
          </button>
        ))}
      </div>
    );

    return () => {
      if (props.presetsOnly) {
        return props.isMobile ? (
          <div class="bg-base-100">{presetChips()}</div>
        ) : (
          <div class="bg-base-100">{presetRail()}</div>
        );
      }

      if (props.isMobile) {
        return (
          <div class="flex flex-col bg-base-100">
            {header()}
            {presetChips()}
            {/* One calendar with both arrows — the split prev/next across two
                stacked calendars was unusable on a phone. */}
            <div class="px-3 py-2">
              <Calendar
                month={props.leftMonth}
                showPrevNav={true}
                showNextNav={true}
                startDate={props.startDate}
                endDate={props.endDate}
                onDateClick={props.onDateClick}
                onMonthChange={props.onLeftMonthChange}
              />
            </div>
          </div>
        );
      }

      return (
        <div class="flex flex-col bg-base-100">
          {header()}
          <div class="flex items-stretch">
            {presetRail()}
            <div class="flex items-start gap-5 p-4">
              <Calendar
                month={props.leftMonth}
                showPrevNav={true}
                showNextNav={false}
                startDate={props.startDate}
                endDate={props.endDate}
                onDateClick={props.onDateClick}
                onMonthChange={props.onLeftMonthChange}
              />
              <div class="w-px self-stretch bg-base-300/70" />
              <Calendar
                month={props.rightMonth}
                showPrevNav={false}
                showNextNav={true}
                startDate={props.startDate}
                endDate={props.endDate}
                onDateClick={props.onDateClick}
                onMonthChange={props.onRightMonthChange}
              />
            </div>
          </div>
        </div>
      );
    };
  },
});
